import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { NewShipFromContactModal } from 'ui-components/components/new-shipfrom-contact-modal';
import { InlineMessageTypePropType } from 'ui-components/components/inline-message';

import { useModal } from '../../../hooks/use-modal';
import { PickupContactPropType } from '../../../prop-types/pickup-contact';
import { ManageShipFromContactModal } from './manage-ship-from-contact-modal';

import { messages } from './messages';
import style from '../dropdown-style.module.less';
import { ContactDropdownItem } from './contact-dropdown-item';

export const ShipFromContactDropdown = ({
  shipFromId = 0,
  className = null,
  required = false,
  contacts,
  selectedContactId = null,
  onContactSelected,
  showDefaultLocationNotes = false,
  variant,
  onBlur,
  hasError = false,
  inlineMessage,
  inlineMessageType = '',
  onContactEdited,
  onContactRemoved,
}) => {
  const { isOpen, open, close } = useModal();
  const { isOpen: isManageShipFromContactOpen, open: openManageShipFromContact, close: closeManageShipFromContact } = useModal();

  const openAddContactModal = useCallback(() => {
    onContactSelected(null);
    open();
  }, [onContactSelected, open]);

  const onContactCreateSuccess = useCallback(
    (contact) => {
      onContactSelected(contact);
      close();
    },
    [onContactSelected, close],
  );

  let dropdownTitle =
    contacts && contacts.length > 0 ? (
      <FormattedMessage {...messages.selectContactDDToggleTitle} />
    ) : (
      <FormattedMessage {...messages.createContactDDToggleTitle} />
    );

  if (selectedContactId && contacts) {
    const selectedContact = contacts.find((contact) => contact.shipFromContactId === selectedContactId);
    if (selectedContact) {
      dropdownTitle = `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
  }

  const isShipFromSelected = shipFromId !== 0;

  return (
    <div className={style['shipfrom-contact-dropdown']}>
      <div className={style['shipfrom-contact-wrapper']}>
        <DropdownMenu
          className={className}
          disabled={!isShipFromSelected}
          hasError={hasError}
          inlineMessage={inlineMessage}
          inlineMessageType={inlineMessageType}
          onBlur={onBlur}
          variant={variant}
        >
          <DropdownMenuToggler required={required}>{dropdownTitle}</DropdownMenuToggler>
          {contacts.map((contact) => (
            <ContactDropdownItem
              key={contact.shipFromContactId}
              contact={contact}
              onContactSelected={onContactSelected}
              selectedContactId={selectedContactId}
            />
          ))}
          <DropdownMenuItem className={className} onClick={openAddContactModal}>
            <div className={style.title}>
              <FormattedMessage {...messages.createTitle} />
            </div>
            <div className={style.subtitle}>
              <FormattedMessage {...messages.createSubTitle} />
            </div>
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
      <Button
        className={style['manage-shipfrom-contact-button']}
        disabled={!isShipFromSelected}
        onClick={openManageShipFromContact}
        testId='shipfrom-contract-manage-button'
        theme={THEME_CONSTANTS.tertiary}
      >
        <FontAwesomeIcon className={style['manage-shipfrom-contact-icon']} icon={faGear} />
      </Button>
      {isOpen && (
        <NewShipFromContactModal
          onCancel={close}
          onClose={close}
          onCreate={onContactCreateSuccess}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      )}
      {isManageShipFromContactOpen && (
        <ManageShipFromContactModal
          contacts={contacts}
          onClose={closeManageShipFromContact}
          onCreate={onContactCreateSuccess}
          onEdit={onContactEdited}
          onRemove={onContactRemoved}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      )}
    </div>
  );
};

ShipFromContactDropdown.propTypes = {
  shipFromId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  required: PropTypes.bool,
  contacts: PropTypes.arrayOf(PickupContactPropType).isRequired,
  selectedContactId: PropTypes.string,
  onContactSelected: PropTypes.func.isRequired,
  onContactEdited: PropTypes.func.isRequired,
  onContactRemoved: PropTypes.func.isRequired,
  showDefaultLocationNotes: PropTypes.bool,
  variant: PropTypes.string,
  hasError: PropTypes.bool,
  inlineMessage: PropTypes.node,
  inlineMessageType: InlineMessageTypePropType,
  onBlur: PropTypes.func,
};
